import { passwordSchema } from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, Link2Off } from 'lucide-react';
import { useEffect, useId, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { AuthScreen } from '@/components/layout/AuthScreen';
import { ErrorState } from '@/components/states/States';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, PasswordInput, fieldA11y } from '@/components/ui/form';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { clearHash, PasswordRules, tokenFromHash } from '@/features/auth/password';
import { SessionLoadingPage } from './AccessPages';

/** "Nova senha" (RF-02): aberta pelo link do e-mail. */
export function ResetPasswordPage() {
  const { client, state } = useAuth();
  const [token] = useState(tokenFromHash);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmRef = useRef<HTMLInputElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const ids = { password: useId(), confirm: useId(), alert: useId(), rules: useId() };

  const check = useQuery({
    queryKey: ['auth', 'reset-link', token],
    queryFn: () => client.checkResetLink(token),
    // Só depois de restaurar a sessão: se ela termina, o cache é limpo e a conferência recomeçaria.
    enabled: token.length > 0 && state.status !== 'loading',
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  // Foco no título no mesmo commit em que a tela troca (leitor de tela anuncia o resultado).
  useLayoutEffect(() => {
    if (done || linkInvalid) headingRef.current?.focus();
  }, [done, linkInvalid]);

  // Tira o token do endereço (histórico, capturas de tela); ele já está guardado no estado.
  useEffect(clearHash, []);

  // Sucesso primeiro: ao terminar, a sessão local pode acabar e o cache ser limpo — a conferência do
  // link (agora já usado) não pode trocar a tela de sucesso pela de link inválido.
  if (done) {
    return (
      <AuthScreen>
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-green-bg text-status-green-fg">
          <CircleCheck className="size-6" aria-hidden />
        </span>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-5 text-3xl font-extrabold tracking-tight outline-none"
        >
          Senha alterada
        </h1>
        <p className="mt-3 text-sm text-muted-foreground" role="status">
          Pronto: sua nova senha já vale. Por segurança, encerramos as sessões abertas da sua conta em todos
          os aparelhos.
        </p>
        <Button asChild size="lg" className="mt-8 w-full">
          <Link to="/entrar">Entrar com a nova senha</Link>
        </Button>
      </AuthScreen>
    );
  }

  if (token && check.isPending) return <SessionLoadingPage />;
  if (token && check.isError) {
    return (
      <AuthScreen>
        <ErrorState
          error={check.error}
          title="Não foi possível conferir o link"
          onRetry={() => void check.refetch()}
          retrying={check.isFetching}
        />
      </AuthScreen>
    );
  }

  if (!token || check.data === false || linkInvalid) {
    return (
      <AuthScreen>
        <span className="inline-flex size-12 items-center justify-center rounded-full bg-status-red-bg text-status-red-fg">
          <Link2Off className="size-6" aria-hidden />
        </span>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="mt-5 text-3xl font-extrabold tracking-tight outline-none"
        >
          Link inválido ou expirado
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Os links de redefinição valem por tempo limitado e só podem ser usados uma vez. Peça um novo e use o
          mais recente que chegar ao seu e-mail.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <Button asChild>
            <Link to="/esqueci-senha">Pedir um novo link</Link>
          </Button>
          <Button asChild variant="ghost">
            <Link to="/entrar">Voltar para entrar</Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting) return;
    setFormError(null);
    const parsed = passwordSchema.safeParse(password);
    const next: typeof errors = {};
    if (!parsed.success) next.password = parsed.error.issues[0]?.message;
    if (!next.password && confirm !== password) next.confirm = 'As senhas não são iguais.';
    setErrors(next);
    if (next.password) return passwordRef.current?.focus();
    if (next.confirm) return confirmRef.current?.focus();
    setSubmitting(true);
    try {
      await client.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RESET_LINK_INVALID') setLinkInvalid(true);
      else setFormError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthScreen>
      <h1 className="text-3xl font-extrabold tracking-tight">Criar nova senha</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Escolha uma senha que você não usa em outros sites. Depois de salvar, as sessões abertas da sua conta
        serão encerradas.
      </p>
      <form
        noValidate
        onSubmit={onSubmit}
        className="mt-8 flex flex-col gap-5"
        aria-describedby={formError ? ids.alert : undefined}
      >
        <FormField
          id={ids.password}
          label="Nova senha"
          error={errors.password}
          hint={<PasswordRules password={password} />}
        >
          <PasswordInput
            ref={passwordRef}
            {...fieldA11y(ids.password, errors.password, true)}
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((x) => ({ ...x, password: undefined }));
            }}
          />
        </FormField>
        <FormField id={ids.confirm} label="Repita a nova senha" error={errors.confirm}>
          <PasswordInput
            ref={confirmRef}
            {...fieldA11y(ids.confirm, errors.confirm)}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setErrors((x) => ({ ...x, confirm: undefined }));
            }}
          />
        </FormField>
        {formError ? (
          <FormAlert id={ids.alert} tone="error">
            {errorMessage(formError)}
          </FormAlert>
        ) : null}
        <Button type="submit" size="lg" className="mt-1 w-full" loading={submitting}>
          Salvar nova senha
        </Button>
      </form>
    </AuthScreen>
  );
}
